/**
 * The design's modal shell: dimmed backdrop, white 12px card, × top-right.
 * Escape and a backdrop click close it unless `locked` (mid-payment).
 * Keyboard: focus lands on the first field, Tab / Shift+Tab stay inside the
 * card, and focus goes back to whatever opened the modal when it closes.
 */

import React, { useEffect, useRef } from 'react';

/** How the visitor closed the modal — for tracking. */
export type ModalCloseVia = 'close' | 'escape' | 'backdrop';

interface Props {
  labelledBy: string;
  onClose: (via: ModalCloseVia) => void;
  locked?: boolean;
  maxWidth?: number;
  /** Extra class on the card (e.g. the payment sheet's roomier padding). */
  className?: string;
  children: React.ReactNode;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const IprModal: React.FC<Props> = ({ labelledBy, onClose, locked, maxWidth = 440, className, children }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tabbables = (): HTMLElement[] =>
      Array.from(cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) || [])
        // Skip the honeypots and anything not rendered.
        .filter(el => el.tabIndex >= 0 && !el.closest('.ipr-hp') && !el.hidden && el.getClientRects().length > 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !lockedRef.current) { closeRef.current('escape'); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const list = tabbables();
      if (!list.length) { e.preventDefault(); cardRef.current.focus(); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = !!active && cardRef.current.contains(active);
      if (e.shiftKey && (!inside || active === first || active === cardRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (!inside || active === last)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    // Focus the first field (or the card) so keyboard users land inside.
    const first = cardRef.current?.querySelector<HTMLElement>('input:not([tabindex="-1"]), select, textarea, button:not(.ipr-modal-close)');
    (first || cardRef.current)?.focus();
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
      // Back to the button that opened it, if it is still on the page.
      try { if (opener && document.contains(opener)) opener.focus({ preventScroll: true }); } catch { /* ignore */ }
    };
  }, []);

  return (
    <div
      className="ipr-modal-backdrop"
      onMouseDown={e => { if (e.target === e.currentTarget && !locked) onClose('backdrop'); }}
    >
      <div
        ref={cardRef}
        className={`ipr-modal${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        style={{ maxWidth }}
      >
        <button
          type="button"
          className="ipr-modal-close"
          aria-label="Close"
          onClick={() => onClose('close')}
          disabled={locked}
          style={locked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
        >×</button>
        {children}
      </div>
    </div>
  );
};
